import { CurrencyPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnChanges } from '@angular/core';
import { AnnuityPensionAnnuityTypeIdEnum } from '../../../../models/enum/shared/annuity-pension-annuity-type-id.enum';
import { DateConversionUtils } from '../../../../utils/date-conversion-utils';

interface AccountDetailItem {
  label: string;
  value: string;
  subtext?: string;
}

@Component({
  selector: 'app-annuity-review-account-details',
  templateUrl: './annuity-review-account-details.component.html',
  styleUrls: ['./annuity-review-account-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AnnuityReviewAccountDetailsComponent implements OnChanges {
  @Input() assetsUsed: number | null = null;
  @Input() estimatedPurchasePrice: number | null = null;
  @Input() useEntireBalance = false;
  @Input() annuityStartDate: string | null = null;
  // A 0-1 fraction as returned by the server (e.g. 0.033 for 3.3%), not a percentage number.
  @Input() assumedAnnuityRate: number | null = null;
  @Input() annuityTypeId: AnnuityPensionAnnuityTypeIdEnum | null = null;
  @Input() estMonthlyBenefit: number | null = null;
  @Input() estSurvivorBenefit: number | null = null;
  @Input() jointPensionerName: string | null = null;
  @Input() isMandatoryRetirement = false;
  @Input() smallPensionPayoutOptIn = false;

  // TODO
  public readonly mandatoryRetirementTooltip = $localize`Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.`;

  public accountDetails: AccountDetailItem[] = [];

  private currencyPipe = new CurrencyPipe('en-CA');

  public trackByLabel(_: number, detail: AccountDetailItem): string {
    return detail.label;
  }

  public ngOnChanges(): void {
    this.accountDetails = this.buildAccountDetails();
  }

  private buildAccountDetails(): AccountDetailItem[] {
    const isJoint = this.isJointSelection();
    const details: AccountDetailItem[] = [
      {
        label: $localize`Assets Used`,
        value: this.formatCurrency(this.assetsUsed),
        subtext: this.useEntireBalance ? $localize`Entire balance used*` : undefined
      },
      { label: $localize`Annuity Start Month`, value: this.formatMonthYear(this.annuityStartDate) },
      { label: $localize`Est. Purchase Price`, value: this.formatCurrency(this.estimatedPurchasePrice) },
      { label: $localize`Assumed Annuity Rate`, value: this.formatRate(this.assumedAnnuityRate) },
      { label: $localize`Annuity Type`, value: this.annuityTypeLabel() }
    ];

    if (isJoint && this.jointPensionerName) {
      details.push({ label: $localize`Joint Pensioner`, value: this.jointPensionerName });
    }

    details.push({ label: $localize`Est. Monthly Benefit`, value: this.formatMonthlyBenefit(this.estMonthlyBenefit) });

    if (isJoint) {
      details.push({
        label: $localize`Est. Survivor Benefit`,
        value: this.formatMonthlyBenefit(this.estSurvivorBenefit)
      });
    }

    if (this.smallPensionPayoutOptIn) {
      details.push({ label: $localize`Small Pension Payout Check**`, value: $localize`Opted In` });
    }

    if (this.annuityTypeId === AnnuityPensionAnnuityTypeIdEnum.RefundLife) {
      details.push({
        label: $localize`Death Benefit`,
        value: $localize`Balance remaining in account paid to a Beneficiary`
      });
    }

    return details;
  }

  private isJointSelection(): boolean {
    return (
      this.annuityTypeId === AnnuityPensionAnnuityTypeIdEnum.JointLastSurvivorSixty ||
      this.annuityTypeId === AnnuityPensionAnnuityTypeIdEnum.JointLastSurvivorSeventyFive ||
      this.annuityTypeId === AnnuityPensionAnnuityTypeIdEnum.JointLastSurvivorOneHundred
    );
  }

  private annuityTypeLabel(): string {
    switch (this.annuityTypeId) {
      case AnnuityPensionAnnuityTypeIdEnum.LifeOnly:
        return $localize`Life Only`;
      case AnnuityPensionAnnuityTypeIdEnum.RefundLife:
        return $localize`Refund Life`;
      case AnnuityPensionAnnuityTypeIdEnum.JointLastSurvivorSixty:
        return $localize`Joint & Last Survivor 60%`;
      case AnnuityPensionAnnuityTypeIdEnum.JointLastSurvivorSeventyFive:
        return $localize`Joint & Last Survivor 75%`;
      case AnnuityPensionAnnuityTypeIdEnum.JointLastSurvivorOneHundred:
        return $localize`Joint & Last Survivor 100%`;
      default:
        return '–';
    }
  }

  private formatCurrency(value: number | null): string {
    if (value === null) {
      return '–';
    }
    return this.currencyPipe.transform(value) ?? '–';
  }

  private formatMonthlyBenefit(value: number | null): string {
    if (value === null) {
      return '–';
    }
    return $localize`${this.formatCurrency(value)} / mo`;
  }

  // assumedAnnuityRate is a 0-1 fraction as returned by the server (e.g. 0.033 for 3.3%).
  private formatRate(value: number | null): string {
    if (value === null) {
      return '–';
    }
    return `${(value * 100).toFixed(2)}%`;
  }

  private formatMonthYear(dateString: string | null): string {
    if (!dateString) {
      return '–';
    }
    const parts = DateConversionUtils.parseDateOnlyParts(dateString);
    if (!parts) {
      return '–';
    }
    const date = new Date(parts.year, parts.month - 1, parts.day);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }
}
